import { FC, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
  IconButton,
  Card,
  Badge,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import {
  MdAdd,
  MdDelete,
  MdDragIndicator,
  MdSave,
  MdQuiz,
  MdSettings
} from 'react-icons/md';

export interface AdminQuizQuestion {
  id: string;
  question: string;
  type: 'single' | 'multiple' | 'text';
  options?: string[];
  correctAnswer?: string | string[];
  explanation?: string;
}

export interface AdminQuizData {
  title: string;
  description: string;
  questions: AdminQuizQuestion[];
  passingScore: number;
  allowRetake: boolean;
  showCorrectAnswers: boolean;
  isFinalEvaluation?: boolean;
}

interface QuizBuilderProps {
  value: AdminQuizData;
  onChange: (value: AdminQuizData) => void;
  onSave: (data: AdminQuizData) => void;
  isSubmitting?: boolean;
}

const QuizBuilder: FC<QuizBuilderProps> = ({
  value,
  onChange,
  onSave,
  isSubmitting = false
}) => {
  const [quizData, setQuizData] = useState<AdminQuizData>(value);
  const toast = useToast();

  const updateQuizData = (updates: Partial<AdminQuizData>) => {
    const newData = { ...quizData, ...updates };
    setQuizData(newData);
    onChange(newData);
  };

  const addQuestion = () => {
    const newQuestion: AdminQuizQuestion = {
      id: Date.now().toString(),
      question: '',
      type: 'single',
      options: [''],
      correctAnswer: '',
      explanation: ''
    };

    updateQuizData({
      questions: [...quizData.questions, newQuestion]
    });
  };

  const updateQuestion = (id: string, updates: Partial<AdminQuizQuestion>) => {
    const updatedQuestions = quizData.questions.map(q =>
      q.id === id ? { ...q, ...updates } : q
    );
    updateQuizData({ questions: updatedQuestions });
  };

  const deleteQuestion = (id: string) => {
    const updatedQuestions = quizData.questions.filter(q => q.id !== id);
    updateQuizData({ questions: updatedQuestions });
  };

  const addOption = (questionId: string) => {
    const question = quizData.questions.find(q => q.id === questionId);
    if (!question || !question.options) return;

    const updatedQuestions = quizData.questions.map(q =>
      q.id === questionId
        ? { ...q, options: [...q.options!, ''] }
        : q
    );
    updateQuizData({ questions: updatedQuestions });
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    const question = quizData.questions.find(q => q.id === questionId);
    if (!question || !question.options) return;

    const updatedOptions = [...question.options];
    updatedOptions[optionIndex] = value;

    const updatedQuestions = quizData.questions.map(q =>
      q.id === questionId
        ? { ...q, options: updatedOptions }
        : q
    );
    updateQuizData({ questions: updatedQuestions });
  };

  const deleteOption = (questionId: string, optionIndex: number) => {
    const question = quizData.questions.find(q => q.id === questionId);
    if (!question || !question.options || question.options.length <= 1) return;

    const updatedOptions = question.options.filter((_, index) => index !== optionIndex);
    const updatedQuestions = quizData.questions.map(q =>
      q.id === questionId
        ? { ...q, options: updatedOptions }
        : q
    );
    updateQuizData({ questions: updatedQuestions });
  };

  const handleSave = () => {
    // Validation
    if (!quizData.title.trim()) {
      toast({
        title: 'Quiz title required',
        description: 'Please enter a quiz title',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (quizData.questions.length === 0) {
      toast({
        title: 'Questions required',
        description: 'Please add at least one question',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate each question
    for (const question of quizData.questions) {
      if (!question.question.trim()) {
        toast({
          title: 'Question text required',
          description: 'All questions must have text',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (question.type !== 'text' && (!question.options || question.options.length === 0)) {
        toast({
          title: 'Options required',
          description: 'Multiple choice questions must have options',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    onSave(quizData);
  };

  const getQuestionTypeLabel = (type: string) => {
    switch (type) {
      case 'single': return 'Single Choice';
      case 'multiple': return 'Multiple Choice';
      case 'text': return 'Text Answer';
      default: return type;
    }
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'single': return 'blue';
      case 'multiple': return 'green';
      case 'text': return 'purple';
      default: return 'gray';
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Quiz Settings */}
      <Card p={4}>
        <VStack spacing={4} align="stretch">
          <HStack>
            <MdSettings />
            <Text fontWeight="bold">Quiz Settings</Text>
          </HStack>

          <FormControl isRequired>
            <FormLabel>Quiz Title</FormLabel>
            <Input
              value={quizData.title}
              onChange={(e) => updateQuizData({ title: e.target.value })}
              placeholder="Enter quiz title"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={quizData.description}
              onChange={(e) => updateQuizData({ description: e.target.value })}
              placeholder="Enter quiz description"
              rows={3}
            />
          </FormControl>

          <HStack spacing={6}>
            <FormControl>
              <FormLabel>Passing Score (%)</FormLabel>
              <NumberInput
                min={0}
                max={100}
                value={quizData.passingScore}
                onChange={(_, value) => updateQuizData({ passingScore: value })}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Allow Retake</FormLabel>
              <Switch
                isChecked={quizData.allowRetake}
                onChange={(e) => updateQuizData({ allowRetake: e.target.checked })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Show Correct Answers</FormLabel>
              <Switch
                isChecked={quizData.showCorrectAnswers}
                onChange={(e) => updateQuizData({ showCorrectAnswers: e.target.checked })}
              />
            </FormControl>
          </HStack>
        </VStack>
      </Card>

      {/* Questions */}
      <Card p={4}>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between">
            <HStack>
              <MdQuiz />
              <Text fontWeight="bold">Questions ({quizData.questions.length})</Text>
            </HStack>
            <Button
              leftIcon={<MdAdd />}
              colorScheme="blue"
              size="sm"
              onClick={addQuestion}
            >
              Add Question
            </Button>
          </HStack>

          {quizData.questions.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">No questions added yet. Click "Add Question" to get started.</Text>
            </Box>
          ) : (
            <Accordion allowMultiple>
              {quizData.questions.map((question, index) => (
                <AccordionItem key={question.id}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <MdDragIndicator />
                        <Text fontWeight="medium">
                          Question {index + 1}: {question.question || 'Untitled Question'}
                        </Text>
                        <Badge colorScheme={getQuestionTypeColor(question.type)}>
                          {getQuestionTypeLabel(question.type)}
                        </Badge>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel>Question Text</FormLabel>
                        <Textarea
                          value={question.question}
                          onChange={(e) => updateQuestion(question.id, { question: e.target.value })}
                          placeholder="Enter your question"
                          rows={2}
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Question Type</FormLabel>
                        <Select
                          value={question.type}
                          onChange={(e) => updateQuestion(question.id, {
                            type: e.target.value as 'single' | 'multiple' | 'text',
                            options: e.target.value === 'text' ? undefined : [''],
                            correctAnswer: e.target.value === 'text' ? undefined : ''
                          })}
                        >
                          <option value="single">Single Choice</option>
                          <option value="multiple">Multiple Choice</option>
                          <option value="text">Text Answer</option>
                        </Select>
                      </FormControl>

                      {question.type !== 'text' && (
                        <Box>
                          <HStack justify="space-between" mb={2}>
                            <FormLabel mb={0}>Options</FormLabel>
                            <Button
                              size="sm"
                              leftIcon={<MdAdd />}
                              onClick={() => addOption(question.id)}
                            >
                              Add Option
                            </Button>
                          </HStack>

                          <VStack spacing={2}>
                            {question.options?.map((option, optionIndex) => (
                              <HStack key={optionIndex} w="full">
                                <Input
                                  value={option}
                                  onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                                  placeholder={`Option ${optionIndex + 1}`}
                                />
                                <IconButton
                                  aria-label="Delete option"
                                  icon={<MdDelete />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => deleteOption(question.id, optionIndex)}
                                  isDisabled={question.options!.length <= 1}
                                />
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      )}

                      {question.type === 'text' ? (
                        <FormControl>
                          <FormLabel>Expected Answer (optional)</FormLabel>
                          <Textarea
                            value={question.correctAnswer || ''}
                            onChange={(e) => updateQuestion(question.id, { correctAnswer: e.target.value })}
                            placeholder="Enter expected answer or leave blank for manual grading"
                            rows={2}
                          />
                        </FormControl>
                      ) : (
                        <FormControl>
                          <FormLabel>Correct Answer{question.type === 'multiple' ? 's' : ''}</FormLabel>
                          {question.type === 'single' ? (
                            <Select
                              value={question.correctAnswer || ''}
                              onChange={(e) => updateQuestion(question.id, { correctAnswer: e.target.value })}
                            >
                              <option value="">Select correct answer</option>
                              {question.options?.map((option, index) => (
                                <option key={index} value={option}>
                                  {option || `Option ${index + 1}`}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <VStack spacing={2}>
                              {question.options?.map((option, index) => (
                                <HStack key={index}>
                                  <input
                                    type="checkbox"
                                    checked={(question.correctAnswer as string[] || []).includes(option)}
                                    onChange={(e) => {
                                      const currentAnswers = question.correctAnswer as string[] || [];
                                      const newAnswers = e.target.checked
                                        ? [...currentAnswers, option]
                                        : currentAnswers.filter(ans => ans !== option);
                                      updateQuestion(question.id, { correctAnswer: newAnswers });
                                    }}
                                  />
                                  <Text>{option || `Option ${index + 1}`}</Text>
                                </HStack>
                              ))}
                            </VStack>
                          )}
                        </FormControl>
                      )}

                      <FormControl>
                        <FormLabel>Explanation (optional)</FormLabel>
                        <Textarea
                          value={question.explanation || ''}
                          onChange={(e) => updateQuestion(question.id, { explanation: e.target.value })}
                          placeholder="Explain why this is the correct answer"
                          rows={2}
                        />
                      </FormControl>

                      <HStack justify="flex-end">
                        <IconButton
                          aria-label="Delete question"
                          icon={<MdDelete />}
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => deleteQuestion(question.id)}
                        />
                      </HStack>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </VStack>
      </Card>

      <Button
        colorScheme="blue"
        leftIcon={<MdSave />}
        onClick={handleSave}
        isLoading={isSubmitting}
        loadingText="Saving..."
        isDisabled={!quizData.title.trim() || quizData.questions.length === 0}
        size="lg"
      >
        Save Quiz
      </Button>
    </VStack>
  );
};

export default QuizBuilder; 